import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';

function inchesToFtIn(totalInches) {
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches % 12) * 10) / 10;
  return { ft: String(ft), inches: String(inches) };
}

export default function EditClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isImperial } = useAuth();
  const [form, setForm] = useState(null);
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get('/clients/' + id).then(data => {
      setForm(data);
      if (isImperial && data.height) {
        const { ft, inches } = inchesToFtIn(parseFloat(data.height));
        setHeightFt(ft);
        setHeightIn(inches);
      }
    }).catch(() => navigate('/clients'));
  }, [id, navigate, isImperial]);

  function update(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function buildPayload() {
    const payload = { ...form };
    if (isImperial) {
      const ft = parseFloat(heightFt) || 0;
      const inches = parseFloat(heightIn) || 0;
      payload.height = ft > 0 || inches > 0 ? (ft * 12 + inches).toFixed(2) : '';
    }
    return payload;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.put('/clients/' + id, buildPayload());
      navigate('/clients/' + id);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this client and all their data? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete('/clients/' + id);
      navigate('/clients');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  if (!form) return <div className="loading-spinner" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/clients/' + id)}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1 }}>Edit Client</h1>
        <button className="icon-btn" form="edit-form" type="submit" disabled={saving}>
          <Icon name="icon-save.png" alt="Save" />
        </button>
      </div>

      <div className="page-content">
        {error && <div className="error-msg">{error}</div>}
        <form id="edit-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.first_name || ''} onChange={update('first_name')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.last_name || ''} onChange={update('last_name')} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email || ''} onChange={update('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" type="tel" value={form.phone || ''} onChange={update('phone')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input className="form-input" type="number" min="1" max="120" value={form.age || ''} onChange={update('age')} />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select" value={form.gender || ''} onChange={update('gender')}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer_not">Prefer not to say</option>
              </select>
            </div>
          </div>

          {isImperial ? (
            <div className="form-group">
              <label className="form-label">Height (ft / in)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="number" min="0" max="8" placeholder="5" value={heightFt}
                    onChange={e => setHeightFt(e.target.value)} style={{ paddingRight: 32 }} />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 13 }}>ft</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="number" min="0" max="11" placeholder="10" value={heightIn}
                    onChange={e => setHeightIn(e.target.value)} style={{ paddingRight: 32 }} />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 13 }}>in</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Height (cm)</label>
              <input className="form-input" type="number" step="0.1" value={form.height || ''} onChange={update('height')} />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">Weight ({isImperial ? 'lbs' : 'kg'})</label>
              <input className="form-input" type="number" step="0.1" value={form.weight || ''} onChange={update('weight')} />
            </div>
            <div className="form-group">
              <label className="form-label">Body Fat %</label>
              <input className="form-input" type="number" step="0.1" value={form.body_fat || ''} onChange={update('body_fat')} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Fitness Level</label>
            <select className="form-select" value={form.fitness_level || ''} onChange={update('fitness_level')}>
              <option value="">Select</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status || 'active'} onChange={update('status')}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="lead">Lead</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Goal</label>
            <textarea className="form-textarea" value={form.goal || ''} onChange={update('goal')} />
          </div>
          <div className="form-group">
            <label className="form-label">Medical Notes</label>
            <textarea className="form-textarea" value={form.medical_notes || ''} onChange={update('medical_notes')} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>

        <button onClick={handleDelete} disabled={deleting}
          style={{ width: '100%', marginTop: 16, minHeight: 48, background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', color: 'var(--color-primary)', borderRadius: 'var(--radius)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          <Icon name="icon-delete.png" size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {deleting ? 'Deleting...' : 'Delete Client'}
        </button>
      </div>
    </div>
  );
}
